import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-9654ef5c"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["innerHTML"]
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = ["title"]
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { class: "column" }
const _hoisted_6 = { class: "p-4 is-flex is-flex-direction-column" }
const _hoisted_7 = { class: "is-flex is-flex-direction-row is-justify-content-center" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]

import { Check, Close, Delete, Pointer, View } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { computed, PropType, Ref, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { Analysis, AnalysisError, AnalysisErrorStatuses, AnalysisStatus } from '@/Analysis/analysis.entity';
import AnalysisService from '@/Analysis/analysis.service';
import EntryControl from '@/components/EntryControl.vue';
import { Entry, EntrySelection } from '@/Entry/entry.entity';
import EntryService from '@/Entry/entry.service';
import { canApproveAnalysis, canCheckAnalysis, canEditAnalysis, canFlagAnalysisForReview } from '@/helpers/permission';
import { Criterion } from '@/Litigation/criterion.entity';
import { useLoaderStore } from '@/stores/loader';


export default /*#__PURE__*/_defineComponent({
  __name: 'AnalysisCriteria',
  props: {
    analysis: {
        type: Object as PropType<Analysis>,
        required: true,
    },
},
  emits: [
    'start-annotation',
    'stop-annotation',
    'locate-selection',
    'remove-selection',
    'change-status',
    'set-entry',
    'fix-error',
    'ignore-error',
],
  setup(__props, { expose: __expose, emit }) {

const props = __props;

const analysisService = new AnalysisService();
const entryService = new EntryService();



const { analysis } = toRefs(props);



const entryControlRefs = ref();
const activeSection = ref(0);
const annotatedEntry: Ref<{ criterion: Criterion; occurrence: Number }> = ref({ criterion: null, occurrence: null });
const loaderStore = useLoaderStore();
const { t } = useI18n();
const router = useRouter();

const entriesBySection = computed(() => {
    const result: Record<string, Array<{ criterion: Criterion; occurrence: Number; value: Array<string> }>> = {};

    analysis.value.litigation.criteria.forEach((criterion: Criterion) => {
        [...Array(criterion.occurrences).keys()].forEach((occurrence) => {
            const entriesArray: Array<Entry> = Object.values(analysis.value.entries);

            const matchingEntry = entriesArray.find(
                (entry) => entry.criterion.code === criterion.code && entry.occurrence === occurrence + 1,
            );

            result[criterion.section] = result[criterion.section] || [];
            result[criterion.section].push({
                criterion,
                occurrence: occurrence + 1,
                value: matchingEntry !== undefined ? matchingEntry.value : [],
            });
        });
    });

    return Object.fromEntries(Object.entries(result).sort((a, b) => a[0].localeCompare(b[0], 'fr', { numeric: true })));
});

const errorsBySection = computed(() => {
    const errors: Record<string, number> = {};

    analysis.value.errors.forEach((analysisError: AnalysisError) => {
        if ([AnalysisErrorStatuses.UNPROCESSED, AnalysisErrorStatuses.IGNORED].includes(analysisError.status)) {
            errors[analysisError.criterion.section] = errors[analysisError.criterion.section] || 0;
            errors[analysisError.criterion.section]++;
        }
    });

    return errors;
});

const entriesSelections = computed(() => {
    const selections: Record<string, Record<number, Array<EntrySelection>>> = {};

    analysis.value.entries.forEach((entry: Entry) => {
        if (entry.selections.length > 0) {
            selections[entry.criterion.code] = selections[entry.criterion.code] || {};
            selections[entry.criterion.code][entry.occurrence] = entry.selections;
        }
    });

    return selections;
});

const entriesErrors = computed(() => {
    const errors: Record<string, Record<number, AnalysisError>> = {};

    analysis.value.errors.forEach((analysisError: AnalysisError) => {
        if ([AnalysisErrorStatuses.UNPROCESSED, AnalysisErrorStatuses.IGNORED].includes(analysisError.status)) {
            errors[analysisError.criterion.code] = errors[analysisError.criterion.code] || {};
            errors[analysisError.criterion.code][analysisError.occurrence] = analysisError;
        }
    });

    return errors;
});

const onChange = async (criterion: Criterion, occurrence: number, value: Array<string>) => {
    try {
        await entryService.setValue(analysis.value, criterion, occurrence, value);
        emit('set-entry');
    } catch (error) {
        ElMessage.error(t('common.unknownError'));
    }
};

const goToAnalysisList = (): void => {
    router.push({
        name: 'analysis-list',
        params: { litigationId: analysis.value.litigation.litigationId },
    });
};

const checkAnalysis = async (): Promise<void> => {
    if (await updateAnalysisStatus(AnalysisStatus.CHECKING)) {
        ElMessage.success(t('analysis.checkSuccess'));
    } else {
        ElMessage.error(t('analysis.checkError'));
    }

    emit('change-status', AnalysisStatus.CHECKING);
};

const flagAnalysisForReview = async (): Promise<void> => {
    if (await updateAnalysisStatus(AnalysisStatus.TO_REVIEW)) {
        goToAnalysisList();
        ElMessage.success(t('analysis.flagForReviewSuccess'));
    } else {
        ElMessage.error(t('common.unknownError'));
    }

    emit('change-status', AnalysisStatus.TO_REVIEW);
};

const approveAnalysis = async (): Promise<void> => {
    if (await updateAnalysisStatus(AnalysisStatus.APPROVED)) {
        goToAnalysisList();
        ElMessage.success(t('analysis.approveSuccess'));
    } else {
        ElMessage.error(t('common.unknownError'));
    }

    emit('change-status', AnalysisStatus.APPROVED);
};

const updateAnalysisStatus = async (status: AnalysisStatus): Promise<boolean> => {
    try {
        await analysisService.updateStatus(analysis.value.analysisId, status);
        return true;
    } catch (error) {
        return false;
    }
};

const toggleAnnotation = (criterion: Criterion, occurrence: Number) => {
    if (
        annotatedEntry.value.criterion &&
        annotatedEntry.value.criterion.code === criterion.code &&
        annotatedEntry.value.occurrence === occurrence
    ) {
        annotatedEntry.value = { criterion: null, occurrence: null };
        emit('stop-annotation', criterion, occurrence);
    } else {
        annotatedEntry.value = { criterion, occurrence };
        emit('start-annotation', criterion, occurrence);
    }
};

const clearAnnotation = () => {
    annotatedEntry.value.criterion = null;
    annotatedEntry.value.occurrence = null;
};

const locateSelection = (criterion: Criterion, occurrence: number, selection: EntrySelection): void => {
    emit('locate-selection', criterion, occurrence, selection);
};

const removeSelection = async (criterion: Criterion, occurrence: number, selection: EntrySelection) => {
    await entryService.removeSelection(analysis.value, criterion, occurrence, selection);
    emit('remove-selection');
};

const fixError = async (criterion: Criterion, occurrence: number) => {
    await analysisService.fixError(analysis.value.analysisId, criterion, occurrence);
    emit('fix-error');
};

const ignoreError = async (criterion: Criterion, occurrence: number) => {
    await analysisService.ignoreError(analysis.value.analysisId, criterion, occurrence);
    emit('ignore-error');
};

const notifySelection = (criterion: Criterion, occurrence: number, selection: string) => {
    const ref = getEntryControlRef(criterion, occurrence);
    if (
        ref === null ||
        selection === '' ||
        !['integer', 'float', 'date', 'text'].includes(criterion.control) ||
        (ref.control !== '' && ref.control !== undefined)
    ) {
        return;
    }

    ref.setValue(selection);
};

const getEntryControlRef = (criterion: Criterion, occurrence: number) => {
    return entryControlRefs.value.find((ref: typeof EntryControl) => {
        return ref.criterion.code === criterion.code && ref.occurrence === occurrence;
    });
};

__expose({ notifySelection });

// Avoid annotation button selection with tab
document.querySelectorAll('.criterion-title button').forEach((button) => button.setAttribute('tabindex', '-1'));

return (_ctx: any,_cache: any) => {
  const _component_el_badge = _resolveComponent("el-badge")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_link = _resolveComponent("el-link")!
  const _component_el_divider = _resolveComponent("el-divider")!
  const _component_el_popover = _resolveComponent("el-popover")!
  const _component_el_tooltip = _resolveComponent("el-tooltip")!
  const _component_el_collapse_item = _resolveComponent("el-collapse-item")!
  const _component_el_collapse = _resolveComponent("el-collapse")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_el_collapse, {
      modelValue: activeSection.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeSection).value = $event)),
      accordion: "",
      onChange: clearAnnotation
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entriesBySection.value, (entries, section, entryIndex) => {
          return (_openBlock(), _createBlock(_component_el_collapse_item, {
            key: section,
            name: entryIndex
          }, {
            title: _withCtx(() => [
              _createVNode(_component_el_badge, {
                value: errorsBySection.value[section],
                hidden: !(section in errorsBySection.value),
                class: "section-badge",
                type: "warning"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(section), 1)
                ]),
                _: 2
              }, 1032, ["value", "hidden"])
            ]),
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries, ({ criterion, occurrence, value }) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: criterion.code + '-' + occurrence,
                  class: "columns"
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["column is-7 criterion-title", {
                        required: criterion.required,
                        annotated:
                            annotatedEntry.value.criterion &&
                            annotatedEntry.value.criterion.code === criterion.code &&
                            annotatedEntry.value.occurrence === occurrence,
                    }])
                  }, [
                    (_unref(analysis) && _unref(canEditAnalysis)(_unref(analysis)))
                      ? (_openBlock(), _createBlock(_component_el_button, {
                          key: 0,
                          icon: _unref(Pointer),
                          circle: "",
                          size: "small",
                          title: _ctx.$t('analysis.annotate'),
                          tabindex: "-1",
                          onClick: ($event: any) => (toggleAnnotation(criterion, occurrence))
                        }, null, 8, ["icon", "title", "onClick"]))
                      : _createCommentVNode("", true),
                    (criterion.code in entriesSelections.value && occurrence in entriesSelections.value[criterion.code])
                      ? (_openBlock(), _createBlock(_component_el_popover, {
                          key: 1,
                          placement: "left",
                          width: 300,
                          "show-after": 200
                        }, {
                          reference: _withCtx(() => [
                            _createVNode(_component_el_tag, {
                              round: "",
                              size: "small"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(entriesSelections.value[criterion.code][occurrence].length), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          default: _withCtx(() => [
                            _createElementVNode("ul", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entriesSelections.value[criterion.code][occurrence], (selection, selectionIndex) => {
                                return (_openBlock(), _createElementBlock("li", {
                                  key: 
                                        criterion.code +
                                        '-' +
                                        occurrence +
                                        '-' +
                                        selection.startAt +
                                        '-' +
                                        selection.endAt
                                    
                                }, [
                                  _createVNode(_component_el_link, {
                                    type: "primary",
                                    icon: _unref(View),
                                    underline: false,
                                    onClick: ($event: any) => (locateSelection(criterion, occurrence, selection))
                                  }, null, 8, ["icon", "onClick"]),
                                  _createTextVNode(" " + _toDisplayString(selection.text) + " ", 1),
                                  (_unref(analysis) && _unref(canEditAnalysis)(_unref(analysis)))
                                    ? (_openBlock(), _createBlock(_component_el_button, {
                                        key: 0,
                                        type: "danger",
                                        plain: "",
                                        icon: _unref(Delete),
                                        size: "small",
                                        onClick: ($event: any) => (removeSelection(criterion, occurrence, selection))
                                      }, null, 8, ["icon", "onClick"]))
                                    : _createCommentVNode("", true),
                                  (
                                            selectionIndex + 1 !== entriesSelections.value[criterion.code][occurrence].length
                                        )
                                    ? (_openBlock(), _createBlock(_component_el_divider, { key: 1 }))
                                    : _createCommentVNode("", true)
                                ]))
                              }), 128))
                            ])
                          ]),
                          _: 2
                        }, 1024))
                      : _createCommentVNode("", true),
                    (criterion.code in entriesErrors.value && occurrence in entriesErrors.value[criterion.code])
                      ? (_openBlock(), _createBlock(_component_el_popover, {
                          key: 2,
                          placement: "top-start",
                          width: 500,
                          "show-after": 200,
                          effect: "warning-popover"
                        }, {
                          reference: _withCtx(() => [
                            _createVNode(_component_el_tag, {
                              round: "",
                              size: "small",
                              type: "warning",
                              effect: "dark"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" ! ")
                              ]),
                              _: 1
                            })
                          ]),
                          default: _withCtx(() => [
                            (
                                    entriesErrors.value[criterion.code][occurrence].status ===
                                        _unref(AnalysisErrorStatuses).UNPROCESSED &&
                                    _unref(analysis) &&
                                    _unref(canEditAnalysis)(_unref(analysis))
                                )
                              ? (_openBlock(), _createBlock(_component_el_button, {
                                  key: 0,
                                  title: _unref(t)('analysis.fixAction'),
                                  type: "success",
                                  size: "small",
                                  icon: _unref(Check),
                                  circle: "",
                                  onClick: ($event: any) => (fixError(criterion, occurrence))
                                }, null, 8, ["title", "icon", "onClick"]))
                              : _createCommentVNode("", true),
                            (
                                    entriesErrors.value[criterion.code][occurrence].status ===
                                        _unref(AnalysisErrorStatuses).UNPROCESSED &&
                                    _unref(analysis) &&
                                    _unref(canEditAnalysis)(_unref(analysis))
                                )
                              ? (_openBlock(), _createBlock(_component_el_button, {
                                  key: 1,
                                  title: _unref(t)('analysis.ignoreAction'),
                                  type: "info",
                                  size: "small",
                                  icon: _unref(Close),
                                  circle: "",
                                  onClick: ($event: any) => (ignoreError(criterion, occurrence))
                                }, null, 8, ["title", "icon", "onClick"]))
                              : _createCommentVNode("", true),
                            _createTextVNode("  "),
                            _createElementVNode("span", {
                              class: _normalizeClass(`status-${entriesErrors.value[criterion.code][occurrence].status}`),
                              innerHTML: 
                                    entriesErrors.value[criterion.code][occurrence].message.replace(
                                        /\*([^\*]+)\*/g,
                                        '<strong>$1</strong>',
                                    )
                                
                            }, null, 10, _hoisted_1)
                          ]),
                          _: 2
                        }, 1024))
                      : _createCommentVNode("", true),
                    (criterion.help !== null && criterion.help.length > 0)
                      ? (_openBlock(), _createBlock(_component_el_tooltip, {
                          key: 3,
                          "show-after": 200,
                          placement: "left"
                        }, {
                          content: _withCtx(() => [
                            _createElementVNode("div", {
                              innerHTML: criterion.help && criterion.help.replace(/\n/g, '<br>')
                            }, null, 8, _hoisted_2)
                          ]),
                          default: _withCtx(() => [
                            _createVNode(_component_el_tag, {
                              round: "",
                              size: "small",
                              type: "info"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(" ? ")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 2
                        }, 1024))
                      : _createCommentVNode("", true),
                    _createTextVNode("  "),
                    _createElementVNode("span", {
                      title: '[' + criterion.code + '] ' + criterion.title
                    }, [
                      _createTextVNode(_toDisplayString(criterion.title), 1),
                      (criterion.occurrences > 1)
                        ? (_openBlock(), _createElementBlock("sup", _hoisted_4, "#" + _toDisplayString(occurrence), 1))
                        : _createCommentVNode("", true)
                    ], 8, _hoisted_3)
                  ], 2),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(EntryControl, {
                      ref_for: true,
                      ref_key: "entryControlRefs",
                      ref: entryControlRefs,
                      criterion: criterion,
                      occurrence: occurrence,
                      "initial-value": value,
                      disabled: !_unref(analysis) || !_unref(canEditAnalysis)(_unref(analysis)),
                      onChange: onChange
                    }, null, 8, ["criterion", "occurrence", "initial-value", "disabled"])
                  ])
                ]))
              }), 128))
            ]),
            _: 2
          }, 1032, ["name"]))
        }), 128))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("button", {
          class: "button is-secondary mr-6",
          onClick: goToAnalysisList
        }, [
          _createVNode(_component_font_awesome_icon, { icon: "arrow-circle-left" }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('common.back')), 1)
        ]),
        (_unref(analysis) && _unref(canCheckAnalysis)(_unref(analysis)))
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              disabled: _unref(loaderStore).isLoading,
              class: "button is-primary mr-2",
              onClick: checkAnalysis
            }, _toDisplayString(_ctx.$t('analysis.check')), 9, _hoisted_8))
          : _createCommentVNode("", true),
        (_unref(analysis) && _unref(canFlagAnalysisForReview)(_unref(analysis)))
          ? (_openBlock(), _createElementBlock("button", {
              key: 1,
              disabled: _unref(loaderStore).isLoading,
              class: "button is-primary mr-2",
              onClick: flagAnalysisForReview
            }, _toDisplayString(_ctx.$t('analysis.flagForReview')), 9, _hoisted_9))
          : _createCommentVNode("", true),
        (_unref(analysis) && _unref(canApproveAnalysis)(_unref(analysis)))
          ? (_openBlock(), _createElementBlock("button", {
              key: 2,
              disabled: _unref(loaderStore).isLoading,
              class: "button is-primary mr-2",
              onClick: approveAnalysis
            }, _toDisplayString(_ctx.$t('analysis.approve')), 9, _hoisted_10))
          : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})